/*
 * Longeva category-banner carousel.
 *
 * Vanilla JS, no dependencies. Pointer-driven drag, button-driven snap,
 * keyboard arrow navigation, current-slide counter, idempotent init,
 * graceful degradation (CSS scroll-snap is the no-JS fallback).
 *
 * Replaces the Swiper.js dependency on the reference site. Single-purpose,
 * ~3 KB minified, INP-friendly.
 */
(function () {
  'use strict';

  if (typeof document === 'undefined') return;

  function init(viewport) {
    if (viewport.dataset.carouselReady === 'true') return;
    var track = viewport.querySelector('.longeva-categories__track');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.children);
    if (slides.length < 2) return;

    var blockCountAttr = parseInt(viewport.getAttribute('data-block-count'), 10);
    var total = isNaN(blockCountAttr) ? slides.length : blockCountAttr;

    var prevBtn = viewport.parentElement.querySelector('[data-longeva-prev]');
    var nextBtn = viewport.parentElement.querySelector('[data-longeva-next]');
    var counter = viewport.parentElement.querySelector('[data-longeva-counter-current]');

    var index = 0;
    var pointerDown = false;
    var startX = 0;
    var startY = 0;
    var currentX = 0;
    var trackX = 0;
    var startTrackX = 0;
    var pointerId = null;
    var didDrag = false;
    var captureSet = false;
    var DRAG_THRESHOLD = 8;

    function getSlideWidth() {
      var first = slides[0];
      if (!first) return 0;
      var rect = first.getBoundingClientRect();
      var style = window.getComputedStyle(track);
      var gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      return rect.width + gap;
    }

    function getMaxIndex() {
      var slideWidth = getSlideWidth();
      if (slideWidth === 0) return 0;
      var visible = Math.floor(viewport.getBoundingClientRect().width / slideWidth);
      return Math.max(0, slides.length - Math.max(1, visible));
    }

    function applyTransform(x) {
      track.style.transform = 'translate3d(' + Math.round(x) + 'px, 0, 0)';
    }

    function goTo(target, instant) {
      var max = getMaxIndex();
      index = Math.max(0, Math.min(target, max));
      var slideWidth = getSlideWidth();
      trackX = -(index * slideWidth);
      if (instant) {
        track.style.transition = 'none';
        applyTransform(trackX);
        // Force reflow so the next style change is animated
        void track.offsetWidth;
        track.style.transition = '';
      } else {
        applyTransform(trackX);
      }
      updateUi();
    }

    function updateUi() {
      var max = getMaxIndex();
      if (prevBtn) prevBtn.disabled = (index <= 0);
      if (nextBtn) nextBtn.disabled = (index >= max);
      if (counter) counter.textContent = Math.min(total, index + 1);
    }

    // ---- Pointer drag ----
    // Touch pointers participate immediately (mobile swipe). Mouse pointers
    // only become drags after the cursor moves DRAG_THRESHOLD pixels. Before
    // that, click on a child anchor must navigate normally, so we never
    // setPointerCapture on the initial pointerdown for mouse.
    function onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      pointerDown = true;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      currentX = e.clientX;
      startTrackX = trackX;
      didDrag = false;
      captureSet = false;
    }
    function onPointerMove(e) {
      if (!pointerDown) return;
      currentX = e.clientX;
      var dx = currentX - startX;
      var dy = e.clientY - startY;
      if (!didDrag) {
        // Wait until we exceed the threshold AND the motion is mostly
        // horizontal. Vertical motion belongs to page scroll.
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          pointerDown = false;
          return;
        }
        didDrag = true;
        viewport.setAttribute('data-dragging', 'true');
        if (!captureSet && viewport.setPointerCapture) {
          try { viewport.setPointerCapture(pointerId); captureSet = true; } catch (_) {}
        }
      }
      trackX = startTrackX + dx;
      applyTransform(trackX);
      if (e.cancelable) e.preventDefault();
    }
    function onPointerUp() {
      if (!pointerDown) return;
      pointerDown = false;
      viewport.removeAttribute('data-dragging');
      if (didDrag) {
        var slideWidth = getSlideWidth();
        var delta = currentX - startX;
        var snapThreshold = slideWidth * 0.2;
        if (delta < -snapThreshold) goTo(index + 1);
        else if (delta > snapThreshold) goTo(index - 1);
        else goTo(index);
      }
      if (captureSet && viewport.releasePointerCapture && pointerId != null) {
        try { viewport.releasePointerCapture(pointerId); } catch (_) {}
      }
      captureSet = false;
      pointerId = null;
    }

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);
    // Only suppress click when the user actually dragged. Plain clicks on
    // anchor cards must navigate via the link's default behaviour.
    track.addEventListener('click', function (e) {
      if (didDrag) {
        e.preventDefault();
        e.stopPropagation();
        didDrag = false;
      }
    }, true);

    // ---- Buttons ----
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(index - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(index + 1); });

    // ---- Keyboard ----
    viewport.tabIndex = 0;
    viewport.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { goTo(index - 1); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { goTo(index + 1); e.preventDefault(); }
    });

    // ---- Resize ----
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { goTo(index, true); }, 120);
    }, { passive: true });

    // ---- Bootstrap ----
    viewport.dataset.carouselReady = 'true';
    goTo(0, true);
  }

  function bootAll() {
    var viewports = document.querySelectorAll('[data-longeva-carousel]');
    Array.prototype.forEach.call(viewports, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll, { once: true });
  } else {
    bootAll();
  }
  // Re-init after Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', bootAll);
})();
